<template>
  <div
    class="line-item"
    :class="{
      [$class('loading')]: !lineItem,
      'is-mix-match-item': isMixMatchFreebie,
      [$class('updating')]:
        state === 'updating' ||
        (isDeposit && depositItemUpdate.length > 0) ||
        itemDepositUpdating ||
        itemBundleUpdating,
      'line-item--studioshot': lineItem?.tags?.includes('studioshot'),
      'line-item--cooled-shipping':
        lineItem?.product_type === 'cooled_shipping',
      'line-item--bundle': isBundleLine,
    }"
  >
    <div v-if="isBundleLine" class="line-item__bundle">
      <div class="line-item__bundle-header">
        <a class="line-item__link" :href="bundleEditUrl" tabindex="-1">
          <span class="visually-hidden" v-text="lineItem.title" />

          <div class="line-item__bundle-image-container">
            <responsive-image
              class="line-item__bundle-image"
              :image="lineItem.localized_image ?? lineItem.featured_image"
              :lazy-load="false"
              ratio="1:1"
              sizes="64px"
            />
          </div>
        </a>

        <div class="line-item__bundle-content">
          <div class="line-item__bundle-copy">
            <a
              class="line-item__link line-item__bundle-title text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
              :href="bundleEditUrl"
              v-text="lineItem.title"
            />

            <ul v-if="bundleExpanded" class="line-item__bundle-list">
              <li
                v-for="component of bundleComponents"
                :key="component.key"
                class="line-item__bundle-item"
              >
                <div class="line-item__bundle-item-image-container">
                  <responsive-image
                    v-if="component.custom_image || component.image"
                    class="line-item__bundle-item-image"
                    :alt="component.product_title"
                    :image="component.custom_image ?? { url: component.image }"
                    :lazy-load="false"
                    ratio="1:1"
                    sizes="40px"
                  />
                </div>

                <div class="line-item__bundle-item-copy">
                  <p
                    class="line-item__bundle-item-title text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
                    v-text="
                      $string('cart.line_item.bundle_item_title', {
                        replace: {
                          quantity: component.quantity,
                          title: component.product_title,
                        },
                      })
                    "
                  />

                  <p
                    class="line-item__bundle-item-variant text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
                    v-text="formatVariantText(component.options_with_values)"
                  />
                </div>
              </li>
            </ul>
          </div>

          <button
            class="line-item__bundle-toggle text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
            type="button"
            @click="bundleExpanded = !bundleExpanded"
          >
            <span
              v-text="
                bundleExpanded
                  ? $string('cart.line_item.show_less')
                  : $string('cart.line_item.show_all')
              "
            />
          </button>
        </div>

        <button
          class="line-item__remove"
          :class="{ [$class('disabled')]: state === 'updating' }"
          type="button"
          @click="handleRemove"
        >
          <span
            class="visually-hidden"
            v-text="$string('cart.line_item.remove')"
          />

          <icon-bin />
        </button>
      </div>

      <div class="line-item__bundle-price">
        <div class="line-item__bundle-price-info">
          <span
            v-if="lineItem?.unit_price"
            class="line-item__bundle-price-unit text-mobile-paragraph-2xs-regular"
            v-text="bundleUnitPriceString"
          />

          <span
            v-if="bundleDiscountBadge"
            class="line-item__bundle-price-badge text-mobile-paragraph-s-bold"
            v-text="bundleDiscountBadge"
          />
        </div>

        <div class="line-item__bundle-price-sale">
          <span
            v-if="bundleCompareAtPrice"
            class="line-item__bundle-price-compare-at text-mobile-paragraph-s-regular"
            v-text="$formatMoney(bundleCompareAtPrice)"
          />

          <span
            class="line-item__bundle-price-final text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            :class="{
              'line-item__bundle-price-final--discounted':
                bundleDiscountPercentage > 0,
            }"
            v-text="$formatMoney(lineItem?.final_price)"
          />
        </div>
      </div>
    </div>

    <div v-else class="line-item__grid">
      <a class="line-item__link" :href="lineItem.url" tabindex="-1">
        <span class="visually-hidden" v-text="lineItem.product_title" />

        <div class="line-item__image-container">
          <responsive-image
            class="line-item__image"
            :image="lineItem.localized_image ?? lineItem.featured_image"
            :lazy-load="false"
            :ratio="$variable('product.imageRatio')"
            sizes="(min-width: 1024px) 107px, 101px"
            widths="107, 214, 321"
          />
        </div>
      </a>

      <div class="line-item__body">
        <div class="line-item__details">
          <p
            v-if="isMixMatchFreebie"
            class="line-item__bundle-freebie-label text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
            v-text="$string('cart.line_item.bundle_freebie_label')"
          />

          <a
            class="line-item__link line-item__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            :href="lineItem.url"
            v-text="lineItem.product_title"
          />
          <p
            v-if="
              !lineItem.product_has_only_default_variant || lineItem.gift_card
            "
            class="line-item__variant line-item__truncate text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
          >
            <span v-text="variantText" />
            <span v-if="bundleExtraItemName">, </span>

            <span v-if="bundleExtraItemName" v-text="bundleExtraItemName" />
          </p>
          <p
            v-if="lineItemProperties"
            class="line-item__line-item-properties text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-html="lineItemProperties"
          />

          <p class="line-item__price-container">
            <product-prices
              class="line-item__price"
              :compare-at-price="compareAtPrice"
              element="span"
              :price="lineItem?.final_price"
              template="cart"
              :variant-id="lineItem?.variant_id"
              :unit-price="lineItem?.unit_price"
              show-savings-notification
              :unit-price-measurement="
                lineItem?.unit_price_measurement?.reference_unit
              "
            />
          </p>
        </div>

        <div
          class="line-item__actions"
          :class="{
            'line-item__actions--graduated':
              lineItem.graduated_discount_products > 0,
          }"
        >
          <quantity-selector
            v-if="!isMixMatchFreebie"
            class="line-item__quantity-selector"
            :line-item="lineItem"
            :quantity="quantity"
            :state="state"
            :deposit="isDeposit || isCooledShipping"
            :item-dependencies-updating="
              itemDepositUpdating || itemBundleUpdating
            "
            @update-quantity="handleQuantityUpdate"
          />

          <button
            v-if="!isDeposit && !isCooledShipping"
            class="line-item__remove"
            :class="{ [$class('disabled')]: state === 'updating' }"
            type="button"
            @click="handleRemove"
          >
            <span
              class="visually-hidden"
              v-text="$string('cart.line_item.remove')"
            />

            <icon-bin />
          </button>
        </div>
      </div>
    </div>

    <notification-panel
      v-if="notification.display && !isBundleLine"
      class-container="line-item__notification-panel"
      :show-close="false"
      :text="notification.text"
      :title="notification.title"
      type="error"
    />
  </div>
</template>

<script>
/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {Boolean|Object} lineItem - Line item in cart response or order.
 *
 */
import { mapActions, mapState } from 'pinia';

import NotificationPanel from '~async/utils/notification-panel/notification-panel';
import ProductPrices from '~global/product-prices/product-prices';
import QuantitySelector from '~async/quantity-selector/quantity-selector';
import ResponsiveImage from '~global/images/responsive-image';

import IconBin from '~icons/general/bin.svg';

import { computeDiscountLogic } from '~/helpers/vue';
import { getMaxQuantityErrorMessage } from '~/helpers/general';
import { formatCartError } from '~/helpers/format-cart-error';
import {
  BUNDLE_ATTRIBUTE,
  BUNDLE_BOX_ATTRIBUTE,
  BUNDLE_BOX_PARAM,
  BUNDLE_GIFT_ATTRIBUTE,
} from '~/helpers/mix-and-match';

import { formatTrackingItem } from '~/format/format-tracking-item';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useCartStore from '~stores/cart';
import useToastNotificationsStore from '~stores/toast-notifications';
import { stripTrailingParenthetical } from '~/format/format-variant';

export default {
  name: 'LineItem',

  components: {
    IconBin,
    NotificationPanel,
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
  },

  props: {
    lineItem: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    location: {
      type: String,
      default: 'cart-drawer',
    },
  },

  data() {
    return {
      bundleExpanded: false,
      error: '',
      quantity: 1,
      state: 'loading',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
    ...mapState(useCartStore, {
      cart: 'response',
      cartStatus: 'status',
      bundleItemUpdate: 'bundleItemUpdate',
      depositItemUpdate: 'depositItemUpdate',
    }),

    /**
     * Checks whether line item is deposit product.
     */
    isDeposit() {
      return (
        `${this.lineItem.id}` === this.$variable('product.depositProductID')
      );
    },

    isCooledShipping() {
      return this.lineItem?.product_type === 'cooled_shipping';
    },

    itemDepositUpdating() {
      return (
        this.lineItem.can_deposit &&
        this.depositItemUpdate.includes(this.lineItem.variant_id)
      );
    },

    itemBundleUpdating() {
      return this.bundleItemUpdate.includes(this.lineItem.id);
    },

    /**
     * Compute compare at price.
     * - Uses compare at price if it exists.
     * - Otherwise if script discount has been applied use original price.
     * - This way it always shows the largest original price.
     * - Otherwise show no compare at price.
     * @returns {Number|Boolean}
     */
    compareAtPrice() {
      if (this.lineItem?.compare_at_price) {
        return this.lineItem?.compare_at_price;
      }

      if (this.lineItem?.original_price !== this.lineItem?.final_price) {
        return this.lineItem?.original_price;
      }

      return false;
    },

    /**
     * Compute if line item is limited by inventory quantity.
     * @returns {Boolean}
     */
    limitedInventory() {
      return (
        this.lineItem.inventory_management &&
        this.lineItem.inventory_policy === 'deny'
      );
    },

    /**
     * Compute line item properties based on theme setting.
     * - Only display those in setting in the order they're added.
     * - Won't display line item if value is blank or false.
     * @returns {String}
     */
    lineItemProperties() {
      const propertiesSetting = this.$variable('settings.lineItemProperties')
        ? this.$variable('settings.lineItemProperties').trim()
        : '';

      if (
        !propertiesSetting ||
        !this.lineItem.properties ||
        !Object.keys(this.lineItem.properties).length
      ) {
        return '';
      }

      // Translation map for property names
      const hiddenPropertiesMappedTranslations = {};

      const translationMap = {
        'Recipient name': 'cart.line_item.recipient_name',
        'Recipient email': 'cart.line_item.recipient_email',
        Message: 'cart.line_item.message',
        'Send on': 'cart.line_item.send_on',
      };

      Object.entries(translationMap).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        const hiddenProperties = `__${key.replace(/ /g, '_').toLowerCase()}`;

        hiddenPropertiesMappedTranslations[hiddenProperties] = value;
      });

      const properties = propertiesSetting
        .split('\n')
        .map((property) => {
          const trimmedProperty = property.trim();
          const trimmedHiddenProperty = `__${trimmedProperty
            .replace(/ /g, '_')
            .toLowerCase()
            .trim()}`;
          const translatedKey =
            this.$string(
              hiddenPropertiesMappedTranslations[trimmedHiddenProperty],
            ) || trimmedProperty;
          const value =
            this.lineItem.properties[trimmedProperty] ||
            this.lineItem.properties[trimmedHiddenProperty];
          if (!value || (typeof value === 'string' && !value.trim().length)) {
            return false;
          }

          return typeof value === 'boolean'
            ? `<div><strong>${translatedKey}</strong></div>`
            : `<div><strong>${translatedKey}</strong>: ${value}</div>`;
        })
        .filter(Boolean);

      return properties.join('');
    },

    /**
     * Compute notification state.
     * @returns {Object}
     */
    notification() {
      if (!this.lineItem || (!this.limitedInventory && !this.error)) {
        return { display: false };
      }

      const notification = {};

      /**
       * Display error message from cart response.
       */
      if (this.error) {
        notification.display = true;
        notification.text = this.error;
        notification.title = '';
        return notification;
      }

      /**
       * Display manual error notifications.
       */
      if (this.lineItem.quantity > this.lineItem.inventory_quantity) {
        notification.display = true;
        notification.title = this.$string(
          'cart.notifications.limited_stock.title',
        );

        notification.text = this.$string(
          'cart.notifications.limited_stock.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        );
      } else if (this.lineItem.quantity === this.lineItem.inventory_quantity) {
        notification.display = true;
        notification.title = this.$string(
          'cart.notifications.maximum_quantity.title',
        );

        notification.text = this.$string(
          'cart.notifications.maximum_quantity.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        );
      }

      return notification;
    },

    /**
     * Compute variant text.
     * @returns {String}
     */
    variantText() {
      if (this.lineItem.gift_card) {
        return this.$formatMoney(this.lineItem.original_price);
      }

      if (!this.lineItem.options_with_values) {
        return this.lineItem.variant_title;
      }

      return this.formatVariantText(this.lineItem.options_with_values);
    },

    /**
     * Show extra bundle item name.
     * Item from the bundle where customers cannot select anything
     */
    bundleExtraItemName() {
      return this.lineItem.properties?.extraBundle?.join(', ') || '';
    },

    /**
     * Cart Transform bundle components (e.g. Mix & Match "shell" line item),
     * merged in from `cart.resource.liquid` via `line_item.item_components`
     * since the AJAX cart.js response doesn't expose them.
     * @returns {Array}
     */
    bundleComponents() {
      return this.lineItem.item_components || [];
    },

    /**
     * Whether this line is a Cart Transform bundle (e.g. Mix & Match),
     * rendered as a components summary card instead of the standard row.
     * @returns {Boolean}
     */
    isBundleLine() {
      return this.bundleComponents.length > 0;
    },

    /**
     * Discount percentage as a number, read from the `Discount` property
     * (e.g. "10%" -> 10). 0 when missing or "0%" — i.e. no real saving,
     * since the discount function still sets this property at 0% when no
     * tier discount applies.
     * @returns {Number}
     */
    bundleDiscountPercentage() {
      return parseFloat(this.lineItem.properties?.Discount) || 0;
    },

    /**
     * Compare-at price for a Mix & Match bundle line.
     * - The Cart Transform merge doesn't set a native `compare_at_price` on
     *   the merged "shell" line — the discount stashes the pre-discount
     *   rolled-up total (major units) in the `_original-total` property
     *   instead, set on the shell line itself (not its item_components).
     * - Hidden when there's no real saving so we don't show a strikethrough
     *   price identical to the current one.
     * @returns {Number|Boolean}
     */
    bundleCompareAtPrice() {
      const originalTotal = this.lineItem.properties?.['_original-total'];

      if (this.bundleDiscountPercentage <= 0 || !originalTotal) {
        return false;
      }

      return Math.round(parseFloat(originalTotal) * 100);
    },

    /**
     * Formatted "amount/unit" price for the bundle price row's kg price.
     * @returns {String}
     */
    bundleUnitPriceString() {
      const unit = (
        this.lineItem?.unit_price_measurement?.reference_unit || ''
      ).toLowerCase();

      return this.$string('product.price.unit_price_clean', {
        replace: {
          amount: this.$formatMoney(this.lineItem?.unit_price),
          unit,
        },
      });
    },

    /**
     * Discount badge text (e.g. "-10%"), hidden when there's no real saving.
     * @returns {String}
     */
    bundleDiscountBadge() {
      return this.bundleDiscountPercentage > 0
        ? `-${this.lineItem.properties.Discount.replace('%', ' %')}`
        : '';
    },

    /**
     * Main product URL for a Mix & Match bundle line, with the box id so
     * the PDP reopens the builder pre-filled with this box (see
     * `getBoxIdFromUrl`/`setBoxIdInUrl` in `helpers/mix-and-match`).
     * @returns {String}
     */
    bundleEditUrl() {
      const boxId = this.lineItem.properties?.[BUNDLE_BOX_ATTRIBUTE];

      if (!this.lineItem.url || !boxId) {
        return this.lineItem.url;
      }

      const url = new URL(this.lineItem.url, window.location.origin);
      url.searchParams.set(BUNDLE_BOX_PARAM, boxId);

      return `${url.pathname}${url.search}`;
    },

    /**
     * Mix & Match bundle config matching this line's `_mix-n-match-bundle`
     * property, or null when this isn't a Mix & Match box line.
     * @returns {Object|null}
     */
    mixMatchBundle() {
      const boxId = this.lineItem.properties?.[BUNDLE_BOX_ATTRIBUTE];

      if (!boxId) {
        return null;
      }

      const bundleId = this.lineItem.properties?.[BUNDLE_ATTRIBUTE];

      return (
        (cnvs.discount?.mixMatch?.bundles || []).find(
          (candidate) => String(candidate?.id) === String(bundleId),
        ) || null
      );
    },

    /**
     * Due to the cart transform, the freebie of a bundle will always be
     * a separate line item, therefore its quantity is derived from the box's
     * reward tier, not user-editable, so the quantity selector is hidden for it.
     * - Detected via `BUNDLE_GIFT_ATTRIBUTE`, set on the line by
     *   `buildBoxCartLines` when the gift is added (see mix-and-match.vue's
     *   `saveBox`), so it stays identifiable at all times - unlike matching
     *   `giftVariantIds` on the bundle config, which only covers *currently*
     *   active bundles and would miss a gift from an expired/past bundle
     *   (e.g. on an old order).
     * @returns {Boolean}
     */
    isMixMatchFreebie() {
      return Boolean(this.lineItem.properties?.[BUNDLE_GIFT_ATTRIBUTE]);
    },

    /**
     * This box's free gift line, if any — the Cart Transform merges the rest
     * of the box into this line (see `bundleComponents`), but never the
     * gift, so removing this line would otherwise orphan it in the cart.
     * `null` for the gift line itself, so removing it directly doesn't try
     * to also remove itself.
     * @returns {Object|null}
     */
    mixMatchGiftLine() {
      const boxId = this.lineItem.properties?.[BUNDLE_BOX_ATTRIBUTE];

      if (!boxId || this.isMixMatchFreebie) {
        return null;
      }

      return (
        this.cart.items.find(
          (item) =>
            item.properties?.[BUNDLE_BOX_ATTRIBUTE] === boxId &&
            item.properties?.[BUNDLE_GIFT_ATTRIBUTE],
        ) || null
      );
    },
  },

  watch: {
    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.lineItem.key)) {
        this.state = 'updating';
        return;
      }

      this.state = 'ready';
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'lineItem.quantity'() {
      this.quantity = this.lineItem.quantity;
    },
  },

  mounted() {
    this.state = 'ready';
    this.quantity = this.lineItem.quantity;
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      updateItem: 'updateItem',
      removeItem: 'updateItem',
      getCartAttributeCode: 'getCartAttributeCode',
      setDepositItemUpdate: 'setDepositItemUpdate',
    }),

    ...mapActions(useDiscountCampaignsStore, {
      setCodeState: 'setCodeState',
      setErrorType: 'setErrorType',
    }),

    ...mapActions(useToastNotificationsStore, ['addToast']),

    /**
     * Formats reversed option values as "Flavour, Size" text — shared by the
     * line item itself and each Mix & Match bundle component.
     * @param {Array} [optionsWithValues]
     * @returns {String}
     */
    formatVariantText(optionsWithValues) {
      if (!optionsWithValues) {
        return '';
      }

      return optionsWithValues
        .slice()
        .reverse()
        .map((option) => {
          const trimmedOptionValue = stripTrailingParenthetical(option.value);
          return this.$string('cart.line_item.variant', {
            replace: {
              selected_value: trimmedOptionValue,
            },
          });
        })
        .join(', ');
    },

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      const oldQuantity = this.quantity;
      const addDeposit =
        this.lineItem.can_deposit && this.$variable('store.country') === 'DE';
      this.quantity = quantity;

      try {
        this.error = '';
        this.state = 'updating';

        if (addDeposit) {
          this.setDepositItemUpdate([
            ...this.depositItemUpdate,
            this.lineItem.variant_id,
          ]);
        }

        if (this.quantity === 0 && addDeposit) {
          await this.addRemoveDepositProducts(this.lineItem.key);
        }

        await this.updateItem([
          {
            id: this.lineItem.key,
            quantity: this.quantity,
          },
        ]);

        if (this.quantity > 0 && addDeposit) {
          await this.addRemoveDepositProducts();
        }

        // Tracking event add/remove qty to item in cart
        if (this.quantity > oldQuantity) {
          this.handleTrackingAddRemoveItem(
            this.quantity - oldQuantity,
            'add_to_cart',
          );
        } else {
          this.handleTrackingAddRemoveItem(
            oldQuantity - this.quantity,
            'remove_from_cart',
          );
        }

        // sets it to the original cart.json quantity
        this.quantity = this.lineItem.quantity;
        this.state = 'ready';
      } catch (error) {
        this.quantity = this.lineItem.quantity;

        // Handle maxQuantity errors
        if (error.maxQuantityError) {
          const message = getMaxQuantityErrorMessage(
            error.maxQuantityType,
            error.maxQuantityValue,
          );

          this.addToast({
            title: message,
            id: 'max-quantity-limit-error',
            type: 'error',
          });
          this.state = 'ready';
          return;
        }

        // Set error state for other errors
        this.state = 'error';

        if (error.description) {
          this.handleErrorMessage(error.description);
          return;
        }

        cnvs.ReportError('Failed to update line item quantity', error);
      }

      if (addDeposit) {
        this.setDepositItemUpdate(
          this.depositItemUpdate.filter(
            (item) => item !== this.lineItem.variant_id,
          ),
        );
      }
    },

    /**
     * Handle remove click.
     */
    async handleRemove() {
      if (this.state === 'updating') {
        return;
      }

      // Tracking event remove item from cart
      this.handleTrackingAddRemoveItem(
        this.lineItem.quantity,
        'remove_from_cart',
      );

      if (
        this.lineItem.can_deposit &&
        this.$variable('store.country') === 'DE'
      ) {
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          this.lineItem.variant_id,
        ]);
        await this.addRemoveDepositProducts(this.lineItem.key);
      }

      try {
        await this.removeItem(this.lineItem.key);
      } finally {
        // Re-read the gift line *after* the box is removed (and any
        // discount/freebie sync that ran as part of that request has
        // settled) rather than a key snapshotted beforehand — a code like
        // an order-level discount can trigger cart recalculation between
        // the two removals, and a `finally` here ensures the gift removal
        // still fires even if the box's own removal request rejected.
        const giftLineKey = this.mixMatchGiftLine?.key;

        if (giftLineKey) {
          await this.removeItem(giftLineKey);
        }
      }

      this.setDepositItemUpdate(
        this.depositItemUpdate.filter(
          (item) => item !== this.lineItem.variant_id,
        ),
      );

      this.setErrorType('');
      this.setCodeState('ready');
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      this.error = formatCartError(errorDescription, '<br>');
    },

    /**
     * Updates the amount of deposit product to add or remove
     */
    async addRemoveDepositProducts(productRemoved = false) {
      // Initialize the total deposit amount
      let totalDepositQuantity = 0;
      let depositProduct = null;

      // Loop over every item in the cart
      this.cart.items.forEach((item) => {
        if (this.$variable('product.depositProductID') === `${item.id}`) {
          depositProduct = item.key;
        }

        // Accumulate the total deposit quantity
        if (item.can_deposit && item.key !== productRemoved) {
          totalDepositQuantity += item.quantity * item.can_deposit_amount;
        }
      });

      const depositProductExists = this.cart.items.some(
        (item) => `${item.key}` === depositProduct,
      );

      const removeDepositProduct = async () => {
        if (depositProductExists) {
          await this.removeItem([{ id: depositProduct, quantity: 0 }]);
        }
      };

      if (productRemoved) {
        const filteredItems = this.cart.items.filter((item) => {
          return item.key !== productRemoved && item.key !== depositProduct;
        });
        if (filteredItems.length === 0) {
          await removeDepositProduct();
          return;
        }
      }

      if (totalDepositQuantity > 0) {
        if (depositProductExists) {
          await this.updateItem([
            { id: depositProduct, quantity: totalDepositQuantity },
          ]);
        } else {
          await this.addItem([
            {
              id: this.$variable('product.depositProductID'),
              quantity: totalDepositQuantity,
            },
          ]);
        }
      } else {
        await removeDepositProduct();
      }
    },

    /**
     * Tracking event add qty to item in cart.
     * @param {Number} qty - Quantity of item added
     * @param {String} eventType - Event type can be:
     * add_to_cart, remove_from_cart
     */
    handleTrackingAddRemoveItem(qty, eventType) {
      const price = this.lineItem.price;
      const compareAtPrice = this.lineItem.compare_at_price;
      const productId = this.lineItem.product_id;
      const discountLogic = computeDiscountLogic({
        discountPreview: false,
        onSale: compareAtPrice > price,
        product: { id: productId },
        variant: this.lineItem,
      });
      const campaignDiscount = this.getCampaignDiscount(
        discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        variant: this.lineItem,
        template: this.location,
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: qty,
      });

      cnvs.EventBus.emit(`dataLayer:${eventType}`, {
        event: eventType,
        currency: cnvs.store.currency,
        location: 'Cart View',
        items: [item],
      });
    },
  },
};
</script>

<style lang="scss">
@import './line-item';
</style>
